import {
  motion,
  useMotionValue,
  useAnimationFrame,
  useInView,
} from "framer-motion";
import { useEffect, useRef, useMemo, useState } from "react";

import resources from "../../resources/Content";

export default function PartnershipSection() {
  const containerRef = useRef(null);
  const { partnership } = resources.about;

  const isInView = useInView(containerRef, { margin: "-15%" });
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isInView) setIsMounted(true);
  }, [isInView]);

    const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
<section
  ref={containerRef}
  className="relative w-full py-20 md:py-24 lg:py-32 text-white bg-cover bg-center bg-fixed overflow-hidden border-t border-neutral-800"
  style={{ backgroundImage: `url(${partnership.background})` }}
>
  <div className="absolute opacity-40 inset-0 bg-[#818689] z-0" />

  <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
    {/* Title */}
    <div className="mb-12 md:mb-16 max-w-xl">
      <p className="text-xs sm:text-sm tracking-widest text-white mb-3 md:mb-4">
        {partnership.header.label}
      </p>

      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-semibold leading-tight mb-4 md:mb-6 lg:whitespace-nowrap">
        {partnership.header.title}
        <span className="text-gray-400 ml-2 md:ml-3">
          {partnership.header.highlight}
        </span>
      </h2>

      {/* ✅ Added line */}
              <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-[30px] sm:text-[60px] md:text-[50px] font-light text-[#ffffff] tracking-tight"
        >
          {(partnership.header.text || "").split(" ").map((word, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.07 }}
              className="inline-block mr-2"
            >
              {word}
            </motion.span>
          ))}
        </motion.h2>
    </div>

    {isMounted && (
      <PartnersBelt partners={partnership.partners} />
    )}
  </div>
</section>
  );
}

/* ================= BELT ================= */

function PartnersBelt({ partners }) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);

  const safePartners = useMemo(() => partners || [], [partners]);

  useEffect(() => {
    if (!trackRef.current) return;

    const track = trackRef.current;

    // 🔥 clone content until it overflows container (NO SEAM)
    const fillTrack = () => {
      const containerWidth = containerRef.current.offsetWidth;

      while (track.scrollWidth < containerWidth * 2) {
        const clone = track.innerHTML;
        track.innerHTML += clone;
      }
    };

    fillTrack();
  }, []);

  useEffect(() => {
    let raf;
    let x = 0;

    const speed = 2; //belt speed

    const animate = () => {
      x -= speed;

      if (Math.abs(x) >= trackRef.current.scrollWidth / 2) {
        x = 0; // reset happens OFFSCREEN → invisible
      }

      trackRef.current.style.transform = `translate3d(${x}px,0,0)`;
      raf = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Edge fades */}
      <div className="absolute left-0 top-0 h-full w-12 md:w-24 bg-gradient-to-r from-transparent to-transparent z-10" />
      <div className="absolute right-0 top-0 h-full w-12 md:w-24 bg-gradient-to-l from-transparent to-transparent z-10" />

      {/* Shine */}
      <motion.div
        className="absolute top-0 left-[-30%] h-full w-[30%] bg-gradient-to-r from-transparent via-white/30 to-transparent blur-2xl z-10"
        animate={{ x: ["0%", "400%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />

      <div
        ref={trackRef}
        className="flex w-max gap-12 md:gap-16 lg:gap-24"
        style={{ willChange: "transform" }}
      >
        {safePartners.map((partner) => (
          <LogoItem
            key={partner.id}
            logo={partner.logo}
            name={partner.name}
          />
        ))}
      </div>
    </div>
  );
}

/* ================= LOGO ================= */

function LogoItem({ logo, name }) {
  return (
    <motion.div
      whileHover={{ scale: 1.08 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="flex items-center justify-center whitespace-nowrap mx-6 sm:mx-10 md:mx-16 lg:mx-20 my-6 md:my-10"
      style={{ willChange: "transform" }}
    >
      <img
        src={logo}
        alt={name}
        loading="lazy"
        decoding="async"
        className="h-12 sm:h-16 md:h-20 lg:h-28 object-contain opacity-90"
      />
    </motion.div>
  );
}